import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { makeStyles, Paper, Theme, Typography } from "@material-ui/core";
import {
  AppBarConfirmButton,
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import { useDispatch, useSelector } from "react-redux";
import { EventNotification, toApiModel } from "../../domain/EventNotification";
import { createItem, inputtingItem } from "../../redux/actions";
import { authSelector, createItemSelector } from "../../redux/selectors";
import { ModuleAppBar } from "../ModuleAppBar";
import { RegulatorsToNotify } from "../sections/RegulatorsToNotify";
import { EventDetails } from "../sections/EventDetails";
import { defaultValues } from "./defaultValues";
import { Awareness } from "../sections/Awareness";
import { ExamSeries } from "../sections/ExamSeries";
import { AffectedQualifications } from "../sections/AffectedQualifications";
import { AffectedCentres } from "../sections/AffectedCentres";
import { AffectedLearners } from "../sections/AffectedLearners";
import { NatureOfEvent } from "../sections/NatureOfEvent";
import { ActionsTaken } from "../sections/ActionsTaken";
import { SuccessDialog } from "../sections/SuccessDialog";
import { UserType } from "../../redux/types";
import { SectionProps } from "../sections/SectionProps";
import { Compliance } from "../sections/Compliance";
import { Save } from "@material-ui/icons";
import { Send } from "@material-ui/icons";
import { AffectedExamDates } from "../sections/AffectedExamDates";
import { IncorrectResults } from "../sections/IncorrectResults";
import { Risk } from "../sections/Risk";
import { MarksAffected } from "../sections/MarksAffected";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      marginTop: theme.contentOffsets.AFTER_SECONDARY_APPBAR,
    },
    paper: {
      padding: 20,
      marginBottom: 30,
    },
    formTitle: {
      color: theme.palette.grey[500],
      fontSize: "1em",
      marginBottom: 15,
      position: "relative",
      fontWeight: 600,
    },
    formHeading: {
      color: "#9f9f9f",
      fontSize: "1em",
      marginTop: 30,
      marginBottom: 30,
      fontWeight: 600,
      position: "relative",
    },
    formSection: {
      padding: 30,
      boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
      border: "1px solid #ddd",
      backgroundColor: "#fff",
      borderRadius: "3px",
    },
  };
});

export const NewItem: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(inputtingItem());
  }, [dispatch]);

  const classes = useStyles();

  const { userType, token, org } = useSelector(authSelector);

  const {
    handleSubmit,
    errors,
    trigger,
    control,
    formState,
    setError,
    clearErrors,
    setValue,
    getValues,
    watch,
  } = useForm<EventNotification>({
    defaultValues: {
      ...defaultValues,
    },
  });

  const createState = useSelector(createItemSelector);
  const { status } = createState;

  const disabled = status === "CREATING";

  const onSubmit = (model: EventNotification) => {
    if (!model.orgId) {
      model.orgId = userType === UserType.AO ? org.id : null;
    }

    dispatch(createItem(toApiModel(model)));
  };

  const sectionProps: SectionProps<EventNotification> = {
    isCreateMode: true,
    user: { userType, token },
    disabled,
    control,
    errors,
    trigger,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    watch,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
  };

  const { regulatorsAffectedIFATE } = useWatch<EventNotification>({
    control,
    name: ["regulatorsAffectedIFATE"],
  });

  return (
    <>
      {status === "CREATED" && <SuccessDialog />}

      <div className={classes.root}>
        <ModuleAppBar breadcrumbs={[{ label: "New Event Notification" }]}>
          <AppBarValidationIndicator errors={errors} />
          {regulatorsAffectedIFATE ? (
            <AppBarConfirmButton
              Icon={Save}
              onClick={handleSubmit(onSubmit)}
              disabled={disabled}
              dataTest="save-en-button"
              text="Send"
              style={{ minWidth: 110 }}
              tooltip="Send"
            >
              Are you sure you want to share this Event Notification with
              Institute for Apprenticeships and Technical Education (IFATE)?
            </AppBarConfirmButton>
          ) : (
            <AppBarSaveButton
              lifecycleLabels={["Send", "Sending", "Sent"]}
              isSubmitting={createState.status === "CREATING"}
              isSubmitted={createState.lastCrudResult.isSuccess}
              onClick={handleSubmit(onSubmit)}
              disabled={disabled}
              dataTest="save-en-button"
              Icon={Send}
            />
          )}
        </ModuleAppBar>

        <form>
          <Typography variant="h6">Notify</Typography>
          <Paper className={classes.formSection}>
            <RegulatorsToNotify {...sectionProps} />
          </Paper>

          <Typography variant="h6">Main Details</Typography>
          <Paper className={classes.formSection}>
            <EventDetails {...sectionProps} />
          </Paper>

          { userType !== UserType.AO && (
            <>
          <Typography variant="h6">Risk</Typography>
          <Paper className={classes.formSection}>
            <Risk {...sectionProps} />
          </Paper>
          </>
          )} 

          <Typography variant="h6">Affected Exam Series</Typography>
          <Paper className={classes.formSection}>
            <ExamSeries {...sectionProps} />
          </Paper>

          <Typography variant="h6">Affected assessment dates</Typography>
          <Paper className={classes.formSection}>
            <AffectedExamDates {...sectionProps} />
          </Paper>

          <Typography variant="h6">Awareness</Typography>
          <Paper className={classes.formSection}>
            <Awareness {...sectionProps} />
          </Paper>

          <Typography variant="h6">Affected - Qualifications</Typography>
          <Paper className={classes.formSection}>
            <AffectedQualifications {...sectionProps} />
          </Paper>

          {/* <h6 className={classes.formHeading}>Affected - Units</h6>

          <Paper className={classes.formSection}>
            <ArraySection
              {...sectionProps}
              fieldLabel="Details of Unit Affected"
              fieldName="affectedUnits"
              addButtonLabel="Add Unit"
            />
          </Paper>*/}

          <Typography variant="h6">Affected - Centres</Typography>
          <Paper className={classes.formSection}>
            <AffectedCentres {...sectionProps} />
          </Paper>

          <Typography variant="h6">Affected - Learners</Typography>
          <Paper className={classes.formSection}>
            <AffectedLearners {...sectionProps} />
          </Paper>

          <Typography variant="h6">Nature of Event</Typography>
          <Paper className={classes.formSection}>
            <NatureOfEvent {...sectionProps} />
          </Paper>

          <Typography variant="h6">No. of Marks</Typography>
          <Paper className={classes.formSection}>
            <MarksAffected {...sectionProps} />
          </Paper>

          <Typography variant="h6">Incorrect Results</Typography>
          <Paper className={classes.formSection}>
            <IncorrectResults {...sectionProps} />
          </Paper>

          <Typography variant="h6">Actions Taken</Typography>
          <Paper className={classes.formSection}>
            <ActionsTaken {...sectionProps} />
          </Paper>

          <Typography variant="h6">Compliance</Typography>
          <Paper className={classes.formSection}>
            <Compliance {...sectionProps} />
          </Paper>
        </form>
      </div>
    </>
  );
};
